/**
 * Control Config Plugin Registry
 *
 * Plugins provide custom UI enhancements for controls with complex visualizations
 * They wrap/extend GenericConfigPanel with additional dashboards and interactive elements
 *
 * Usage:
 * import { getControlPlugin } from './plugins';
 * const Plugin = getControlPlugin('CTRL_01');
 * if (Plugin) {
 *   return <Plugin projectId={projectId} controlCode={controlCode} />;
 * }
 */

import CTRL_01_Plugin from './CTRL_01_Plugin';
import CTRL_02_Plugin from './CTRL_02_Plugin';
import CTRL_03_Plugin from './CTRL_03_Plugin';
import CTRL_04_Plugin from './CTRL_04_Plugin';
import CTRL_05_Plugin from './CTRL_05_Plugin';
import CTRL_06_Plugin from './CTRL_06_Plugin';
import CTRL_07_Plugin from './CTRL_07_Plugin';
import CTRL_08_Plugin from './CTRL_08_Plugin';
import CTRL_09_Plugin from './CTRL_09_Plugin';
import CTRL_10_Plugin from './CTRL_10_Plugin';
import CTRL_11_Plugin from './CTRL_11_Plugin';
import CTRL_12_Plugin from './CTRL_12_Plugin';
import CTRL_13_Plugin from './CTRL_13_Plugin';
import CTRL_14_Plugin from './CTRL_14_Plugin';

// Plugin registry - map control codes to custom plugin components
const CONTROL_PLUGINS = {
  CTRL_01: CTRL_01_Plugin, // PII Detection with visual dashboard
  CTRL_02: CTRL_02_Plugin, // Data Quality Monitoring with metrics
  CTRL_03: CTRL_03_Plugin, // Infrastructure Event Filtering with stats
  CTRL_04: CTRL_04_Plugin, // Explainability & Transparency with method cards
  CTRL_05: CTRL_05_Plugin, // Training Data Audit with lineage tracking
  CTRL_06: CTRL_06_Plugin, // Model Performance Monitoring with metric cards
  CTRL_07: CTRL_07_Plugin, // Fairness with bias metrics visualization
  CTRL_08: CTRL_08_Plugin, // Human Oversight with review queue
  CTRL_09: CTRL_09_Plugin, // Data Residency with compliance tracking
  CTRL_10: CTRL_10_Plugin, // Documentation Completeness with checklist
  CTRL_11: CTRL_11_Plugin, // Incident Response with tracking dashboard
  CTRL_12: CTRL_12_Plugin, // Purpose Limitation with usage monitoring
  CTRL_13: CTRL_13_Plugin, // Data Provenance with lineage graph
  CTRL_14: CTRL_14_Plugin, // Data Minimization with field analysis
};

/**
 * Get plugin component for a control code
 * @param {string} controlCode - Control code (e.g., 'CTRL_01')
 * @returns {Component|null} Plugin component or null if no plugin exists
 */
export function getControlPlugin(controlCode) {
  return CONTROL_PLUGINS[controlCode] || null;
}

/**
 * Check if control has a custom plugin
 * @param {string} controlCode - Control code
 * @returns {boolean} True if plugin exists
 */
export function hasControlPlugin(controlCode) {
  return controlCode in CONTROL_PLUGINS;
}

/**
 * Get all control codes with plugins
 * @returns {string[]} Array of control codes
 */
export function getPluginControlCodes() {
  return Object.keys(CONTROL_PLUGINS);
}

// Export individual plugins for direct import if needed
export {
  CTRL_01_Plugin,
  CTRL_02_Plugin,
  CTRL_03_Plugin,
  CTRL_04_Plugin,
  CTRL_05_Plugin,
  CTRL_06_Plugin,
  CTRL_07_Plugin,
  CTRL_08_Plugin,
  CTRL_09_Plugin,
  CTRL_10_Plugin,
  CTRL_11_Plugin,
  CTRL_12_Plugin,
  CTRL_13_Plugin,
  CTRL_14_Plugin,
};

export default CONTROL_PLUGINS;
